import React, { useState, useEffect, useRef } from 'react';
import API_URL from '../../config';

const AutocompleteInput = ({
  value,
  onChange,
  placeholder,
  field,
  uppercase = true,
  className = 'ev-input',
  required = false,
  style = {},
  id,
  name
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch suggestions from backend for this field
  useEffect(() => {
    if (!field) return;
    let isMounted = true;
    
    fetch(`${API_URL}/suggestions/${field}`)
      .then(res => res.json())
      .then(data => {
        if (isMounted && data.success && Array.isArray(data.suggestions)) {
          setSuggestions(data.suggestions);
        }
      })
      .catch(() => {});

    return () => {
      isMounted = false;
    };
  }, [field]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setHighlightIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter suggestions: Prioritize startsWith, then includes
  const currentVal = (value || '').trim().toUpperCase();
  const filteredSuggestions = React.useMemo(() => {
    if (!currentVal) {
      return suggestions.slice(0, 6);
    }
    const startsWith = suggestions.filter(s => s.toUpperCase().startsWith(currentVal));
    const contains = suggestions.filter(s => !s.toUpperCase().startsWith(currentVal) && s.toUpperCase().includes(currentVal));
    return [...startsWith, ...contains].slice(0, 8);
  }, [suggestions, currentVal]);

  const handleSelect = (item) => {
    const formatted = uppercase ? item.toUpperCase() : item;
    onChange(formatted);
    setIsOpen(false);
    setHighlightIndex(-1);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (!isOpen || filteredSuggestions.length === 0) {
      if (e.key === 'ArrowDown' && filteredSuggestions.length > 0) {
        setIsOpen(true);
        setHighlightIndex(0);
        e.preventDefault();
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex(prev => (prev < filteredSuggestions.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(prev => (prev > 0 ? prev - 1 : filteredSuggestions.length - 1));
    } else if (e.key === 'Enter') {
      if (highlightIndex >= 0 && highlightIndex < filteredSuggestions.length) {
        e.preventDefault();
        handleSelect(filteredSuggestions[highlightIndex]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setHighlightIndex(-1);
    }
  };

  const renderHighlight = (text, query) => {
    if (!query) return text;
    const idx = text.toUpperCase().indexOf(query.toUpperCase());
    if (idx === -1) return text;
    return (
      <>
        {text.substring(0, idx)}
        <span style={{ color: '#60a5fa', fontWeight: 700, textDecoration: 'underline' }}>
          {text.substring(idx, idx + query.length)}
        </span>
        {text.substring(idx + query.length)}
      </>
    );
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', ...style }}>
      <input
        ref={inputRef}
        id={id}
        name={name}
        className={className}
        value={value || ''}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
        onChange={e => {
          const val = uppercase ? e.target.value.toUpperCase() : e.target.value;
          onChange(val);
          setIsOpen(true);
          setHighlightIndex(-1);
        }}
        onFocus={() => {
          if (filteredSuggestions.length > 0) {
            setIsOpen(true);
          }
        }}
        onKeyDown={handleKeyDown}
      />

      {isOpen && filteredSuggestions.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 1050,
            background: 'var(--card-bg, #151828)',
            border: '1px solid var(--glass-border, rgba(255, 255, 255, 0.15))',
            borderRadius: 10,
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.55), 0 2px 8px rgba(0, 0, 0, 0.25)',
            maxHeight: 220,
            overflowY: 'auto',
            backdropFilter: 'blur(16px)',
            padding: '4px'
          }}
        >
          {filteredSuggestions.map((item, index) => {
            const isHighlighted = index === highlightIndex;
            return (
              <div
                key={`${item}-${index}`}
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent blur before select
                  handleSelect(item);
                }}
                onMouseEnter={() => setHighlightIndex(index)}
                style={{
                  padding: '0.6rem 0.85rem',
                  fontSize: '0.85rem',
                  borderRadius: 6,
                  cursor: 'pointer',
                  color: isHighlighted ? '#ffffff' : 'var(--text-main, #e2e8f0)',
                  background: isHighlighted 
                    ? 'linear-gradient(90deg, rgba(59, 130, 246, 0.25), rgba(139, 92, 246, 0.25))' 
                    : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'background 0.15s ease'
                }}
              >
                <span>{renderHighlight(item, currentVal)}</span>
                {item.toUpperCase().startsWith(currentVal) && currentVal.length > 0 && (
                  <span style={{ fontSize: '0.68rem', color: '#93c5fd', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Match
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AutocompleteInput;
